/**
 * ContactCardPopover — fetches a contact by ID and renders the full
 * CRM ContactEditModal without opening the contacts module.
 *
 * Only supports contact objects. For non-contact types, the parent
 * should use module.focus + selectObject instead.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { http } from '@/lib/http';
import { useSession } from '@/features/auth/hooks/useSession';
import { ContactEditModal } from '@/modules/contacts/ContactEditModal';

interface Props {
  objectId: string;
  objectType: string;
  onClose: () => void;
}

/** API envelope + known mapped fields — pruned from the raw payload before
 *  it's handed to ContactEditModal so the "More" section doesn't surface
 *  plumbing (_etag, moduleId/moduleIds, version, notes, etc.). */
const API_ENVELOPE_KEYS = new Set([
  'id', 'type', 'title', 'name', 'visibility', 'version', '_etag',
  'createdAt', 'updatedAt', 'created_at', 'updated_at',
  'archived_at', 'archivedAt',
  'moduleId', 'moduleIds', 'moduleLinks', 'conversationIds', 'conversationLinks',
  'workspaceId', 'actorId',
  'email', 'emails', 'phone', 'phones',
  'organization', 'company', 'firm',
  'role', 'location', 'city', 'state', 'country', 'address', 'zip',
  'summary', 'notes', 'activities', 'attributes',
  'stage', 'dealValue', 'probability', 'nextStep',
  'authorId', 'authorName',
  'xHandle', 'x_handle', 'linkedinUrl', 'contactTitle',
  'enrichmentSummary', 'enrichmentSkills', 'enrichmentConfidence',
  'displayNameOverride',
]);

/** Extract a plain string from a canonical contact element (string or `{ value }` object). */
function extractContactValue(v: unknown): string {
  if (typeof v === 'string') return v;
  if (v && typeof v === 'object' && typeof (v as { value?: unknown }).value === 'string') {
    return (v as { value: string }).value;
  }
  return '';
}

function pickCustomAttributes(raw: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    if (API_ENVELOPE_KEYS.has(k)) continue;
    out[k] = v;
  }
  return out;
}

export function ContactCardPopover({ objectId, objectType, onClose }: Props) {
  const { token } = useSession();
  const [contact, setContact] = useState<any>(null);

  // Stable wrapper that always dispatches to the current `onClose` prop —
  // the underlying ref avoids stale-closure bugs when the parent re-renders
  // with a new handler, while keeping the identity constant so the fetch
  // effect below doesn't re-fire on every parent render.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);
  const stableOnClose = useCallback(() => { onCloseRef.current(); }, []);

  useEffect(() => {
    if (objectType !== 'contact') {
      // Non-contact types are not supported by this component
      stableOnClose();
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await http.get<any>(`/${objectType}s/${objectId}`, { authToken: token });
        if (!cancelled && res.data) {
          // API returns a flat object with attributes merged at top level
          const raw = res.data;
          const get = (key: string) => raw[key] ?? raw.attributes?.[key];
          setContact({
            id: raw.id,
            type: raw.type,
            name: raw.title || get('name') || '',
            title: raw.title,
            email: extractContactValue(get('email')) || (Array.isArray(get('emails')) ? extractContactValue(get('emails')[0]) : ''),
            emails: Array.isArray(get('emails')) ? (get('emails') as unknown[]).map(extractContactValue).filter(Boolean) : [],
            phone: extractContactValue(get('phone')) || (Array.isArray(get('phones')) ? extractContactValue(get('phones')[0]) : ''),
            phones: Array.isArray(get('phones')) ? (get('phones') as unknown[]).map(extractContactValue).filter(Boolean) : [],
            organization: get('organization') || get('company') || get('firm') || '',
            role: get('role') || '',
            location: get('location') || '',
            summary: get('summary') || '',
            notes: get('notes') || [],
            stage: get('stage') || '',
            dealValue: get('dealValue'),
            probability: get('probability'),
            nextStep: get('nextStep'),
            x_handle: get('xHandle') || get('x_handle') || '',
            version: raw.version,
            _etag: raw._etag || String(raw.version || ''),
            createdAt: raw.createdAt,
            // Propagate displayNameOverride so ContactEditModal can render
            // the caller-supplied casing verbatim instead of auto-casing.
            displayNameOverride: get('displayNameOverride') || null,
            // Pass the API's attribute bag through so ContactEditModal can
            // surface custom fields (relationship, birthday, facebookId, etc.).
            // The API flattens attributes onto the top level, so if there's
            // no nested `attributes`, use the raw payload minus the known
            // envelope/metadata keys that shouldn't surface in "More".
            attributes: (raw.attributes && typeof raw.attributes === 'object')
              ? raw.attributes
              : pickCustomAttributes(raw),
          });
        }
      } catch (err) {
        console.warn('[ContactCardPopover] Failed to fetch:', err);
        stableOnClose();
      }
    })();
    return () => { cancelled = true; };
  }, [objectId, objectType, token, stableOnClose]);

  if (!contact) return null;

  return (
    <ContactEditModal
      contact={contact}
      onClose={onClose}
      onSave={() => {}}
    />
  );
}
